import { useEffect, useRef, useState } from "react";

import { Spinner } from "@/components/ui";
import { supabase } from "@/lib/supabase";

/**
 * Where a magic link lands: `https://crm.cstlegacy.com/auth/callback`.
 *
 * This route has to be reachable **without a session**, because completing it is what creates the
 * session. It is therefore rendered before the session gate in `App`, not inside `AuthedApp` — a
 * callback that only renders once you are signed in is a callback that can never sign you in.
 *
 * It completes both shapes a Supabase magic link can arrive in:
 *
 *   * **PKCE** — `?code=…`. Exchanged explicitly for a session. This needs the code verifier the
 *     client stored when the link was requested, so it only works in the browser that asked for it,
 *     which is the point of PKCE.
 *   * **implicit** — `#access_token=…&refresh_token=…`. `detectSessionInUrl` (set on the client)
 *     consumes the fragment during initialisation, so there is nothing to exchange; the work here is
 *     to *wait* for that to finish rather than to redirect while the session is still arriving.
 *
 * Handling both is not hedging. The shape is decided by the client's `flowType` at request time, and
 * whichever we run today, a callback that only understands one of them breaks silently on the day
 * that changes — and "silently" here means a user clicking a valid link and landing on a login page.
 *
 * An error in the URL is shown rather than swallowed. `otp_expired` and `access_denied` are the two
 * that will actually happen, and both are things the person can act on if they are told.
 */

/** Poll for the session the client is establishing from the URL fragment. */
const IMPLICIT_TIMEOUT_MS = 8000;
const POLL_MS = 150;

type State =
  | { kind: "working" }
  | { kind: "done" }
  | { kind: "failed"; message: string; detail?: string };

function readError(): { message: string; detail?: string } | null {
  // Supabase puts errors in the query string on the PKCE path and in the fragment on the implicit
  // one, so both have to be read.
  const search = new URLSearchParams(window.location.search);
  const hash = new URLSearchParams(window.location.hash.replace(/^#/, ""));
  const code = search.get("error") ?? hash.get("error");
  const errorCode = search.get("error_code") ?? hash.get("error_code");
  const description = search.get("error_description") ?? hash.get("error_description");
  if (!code && !errorCode && !description) return null;

  const friendly: Record<string, string> = {
    otp_expired: "That magic link has expired. Request a new one.",
    access_denied: "That magic link has already been used, or it was not meant for this browser.",
  };
  const key = errorCode ?? code ?? "";
  return {
    message: friendly[key] ?? "That sign-in link could not be used.",
    detail: description ? description.replace(/\+/g, " ") : (errorCode ?? code ?? undefined),
  };
}

/**
 * Complete the sign-in. Runs exactly once per page load; see the effect below for why that matters.
 *
 * Deliberately outside the component and free of React state: it has one job, it is not re-entrant
 * (exchanging a one-time code twice fails), and keeping it a plain async function makes "run this
 * once and share the result" expressible as a single cached promise.
 */
async function completeSignIn(): Promise<State> {
  const urlError = readError();
  if (urlError) return { kind: "failed", ...urlError };

  // Already signed in — a refresh of this URL, or the client finished before the effect ran.
  const existing = await supabase.auth.getSession();
  if (existing.data.session) return { kind: "done" };

  const code = new URLSearchParams(window.location.search).get("code");
  if (code) {
    const { error } = await supabase.auth.exchangeCodeForSession(code);
    if (error) {
      return {
        kind: "failed",
        message: "That sign-in link could not be completed.",
        detail: error.message,
      };
    }
    return { kind: "done" };
  }

  // Implicit: the client consumes the fragment while it initialises, so wait for it rather than
  // deciding it failed. `onAuthStateChange` alone is not enough — if the session landed before we
  // subscribed, no event is ever delivered.
  const deadline = Date.now() + IMPLICIT_TIMEOUT_MS;
  for (;;) {
    const { data } = await supabase.auth.getSession();
    if (data.session) return { kind: "done" };
    if (Date.now() >= deadline) break;
    await new Promise((r) => setTimeout(r, POLL_MS));
  }
  return {
    kind: "failed",
    message: "That sign-in link did not carry a session.",
    detail:
      "The link may have been opened in a different browser from the one that requested it, " +
      "or stripped of its parameters by an email client.",
  };
}

export function AuthCallback() {
  const [state, setState] = useState<State>({ kind: "working" });
  /**
   * The completion, cached so it runs once and survives React 18's double-invoked effect.
   *
   * This was a real bug and an instructive one. The first version guarded with a boolean ref and
   * aborted its async work from the effect's cleanup: in development React mounts, runs the effect,
   * unmounts, and mounts again — so pass one started the work and was then cancelled by its own
   * cleanup, and pass two saw the boolean already set and did nothing at all. The session was sitting
   * in localStorage, correctly established, while the page showed "Signing you in…" forever.
   *
   * Caching the promise instead means the work happens once (the one-time code is never exchanged
   * twice) and *both* passes await the same result. The mounted flag only gates `setState`.
   */
  const completion = useRef<Promise<State> | null>(null);

  useEffect(() => {
    let mounted = true;
    completion.current ??= completeSignIn();
    void completion.current.then((next) => {
      if (!mounted) return;
      setState(next);
      if (next.kind === "done") {
        // `replace`, not `assign`: the URL still carries a used one-time code or token, and it should
        // not sit in history where Back would replay it. A full navigation, so `App` re-reads the
        // session it has just been given.
        window.location.replace("/");
      }
    });
    return () => {
      mounted = false;
    };
  }, []);

  if (state.kind === "failed") {
    return (
      <div className="mx-auto max-w-md p-6" data-testid="auth-callback-error">
        <h1 className="text-lg font-semibold tracking-tight">Could not sign in</h1>
        <p className="mt-2 text-sm">{state.message}</p>
        {state.detail && <p className="mt-1 text-xs text-fg-muted">{state.detail}</p>}
        <a className="mt-4 inline-block text-sm underline" href="/" data-testid="auth-callback-retry">
          Back to sign in
        </a>
      </div>
    );
  }

  return (
    <div data-testid="auth-callback">
      <Spinner label="Signing you in…" />
    </div>
  );
}
